import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import ResponsiveImg from "@/components/ResponsiveImg";

/**
 * MarkdownBody — renders blog post body with Reformity brand styling.
 * - H2: Bebas Neue, brand-dark, large, tracking-wide
 * - H3: Montserrat SemiBold, brand-dark
 * - p: Montserrat, 18px desktop, leading-relaxed, brand-dark
 * - a: brand-cyan, underline-on-hover; external links open in new tab
 * - blockquote: coral left border, italic
 * - code: off-white background, monospace, sharp edges
 */

const isExternal = (href = "") =>
    /^https?:\/\//i.test(href) && !href.startsWith("https://reformityinc.com");

const components = {
    h1: ({ node, className, ...props }) => (
        <h1
            className={cn(
                "font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-tight mt-10 mb-5",
                className,
            )}
            {...props}
        />
    ),
    h2: ({ node, className, ...props }) => (
        <h2
            className={cn(
                "font-display text-3xl sm:text-4xl tracking-wide text-brand-dark leading-tight mt-10 mb-4",
                className,
            )}
            {...props}
        />
    ),
    h3: ({ node, className, ...props }) => (
        <h3
            className={cn(
                "font-sans font-semibold text-xl sm:text-2xl text-brand-dark mt-6 mb-3",
                className,
            )}
            {...props}
        />
    ),
    h4: ({ node, className, ...props }) => (
        <h4
            className={cn(
                "font-sans font-semibold text-lg text-brand-dark mt-5 mb-2",
                className,
            )}
            {...props}
        />
    ),
    p: ({ node, className, ...props }) => (
        <p
            className={cn(
                "text-base md:text-[17px] text-brand-dark/85 leading-relaxed mb-5",
                className,
            )}
            {...props}
        />
    ),
    a: ({ node, href, children, ...props }) => {
        const external = isExternal(href);
        return (
            <a
                href={href}
                className="text-brand-cyan hover:underline font-medium"
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer nofollow" : undefined}
                {...props}
            >
                {children}
            </a>
        );
    },
    ul: ({ node, className, ...props }) => (
        <ul
            className={cn(
                "list-disc ml-6 mb-5 space-y-2 text-base md:text-[17px] text-brand-dark/85 leading-relaxed",
                className,
            )}
            {...props}
        />
    ),
    ol: ({ node, className, ...props }) => (
        <ol
            className={cn(
                "list-decimal ml-6 mb-5 space-y-2 text-base md:text-[17px] text-brand-dark/85 leading-relaxed",
                className,
            )}
            {...props}
        />
    ),
    li: ({ node, className, ...props }) => (
        <li className={cn("pl-1", className)} {...props} />
    ),
    blockquote: ({ node, className, ...props }) => (
        <blockquote
            className={cn(
                "border-l-4 border-brand-coral pl-5 my-6 italic text-brand-dark/80 text-base md:text-[17px] leading-relaxed",
                className,
            )}
            {...props}
        />
    ),
    code: ({ node, inline, className, children, ...props }) => {
        if (inline) {
            return (
                <code
                    className="bg-brand-offwhite border border-brand-dark/10 px-1.5 py-0.5 text-sm font-mono text-brand-dark"
                    {...props}
                >
                    {children}
                </code>
            );
        }
        return (
            <pre className="bg-brand-dark text-white p-4 my-6 overflow-x-auto text-sm font-mono">
                <code {...props}>{children}</code>
            </pre>
        );
    },
    table: ({ node, className, ...props }) => (
        <div className="my-6 overflow-x-auto border border-brand-dark/10">
            <table
                className={cn("w-full text-left text-sm md:text-base", className)}
                {...props}
            />
        </div>
    ),
    thead: ({ node, className, ...props }) => (
        <thead
            className={cn("bg-brand-dark text-white", className)}
            {...props}
        />
    ),
    th: ({ node, className, ...props }) => (
        <th
            className={cn("px-4 py-3 font-semibold uppercase tracking-wider text-xs", className)}
            {...props}
        />
    ),
    td: ({ node, className, ...props }) => (
        <td
            className={cn(
                "px-4 py-3 border-t border-brand-dark/10 text-brand-dark/85",
                className,
            )}
            {...props}
        />
    ),
    hr: () => <hr className="my-10 border-t border-brand-dark/15" />,
    img: ({ node, src, alt, ...props }) => (
        <ResponsiveImg
            src={src}
            alt={alt || ""}
            className="w-full my-6"
            {...props}
            sizes="(min-width: 768px) 720px, 100vw"
        />
    ),
    strong: ({ node, ...props }) => (
        <strong className="font-semibold text-brand-dark" {...props} />
    ),
};

const MarkdownBody = ({ markdown = "", testid = "blog-body" }) => (
    <div className="font-sans" data-testid={testid}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {markdown}
        </ReactMarkdown>
    </div>
);

export default MarkdownBody;
